import { AfterViewInit, ChangeDetectorRef, Component } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { MessageBoxHelper } from '../../components/dialogs/messagebox/messagebox.dialog';
import { ArrayHelpers, TranslateFormatText } from '../../helpers/array.helpers';
import { NotificationHelper } from '../../helpers/notification.helper';
import { MessageBoxButtons } from '../../models/enums/messageboxbuttons.enum';
import { MessageBoxIcon } from '../../models/enums/messageboxicon.enum';
import { MessageBoxResult } from '../../models/enums/messageboxresult.enum';
import { ConfigService } from '../../services/config.service';
import { DBUserService } from '../../services/dbuser.service';
import { MailSettingsService } from '../../services/mailsettings.service';
import { RolesService } from '../../services/roles.service';
import { SecurityService } from '../../services/security.service';
import { UsersService } from '../../services/users.service';
import { DataCheck } from '../../workflow/workflow.dialog';
import { BaseListDetail, BaseListSettings, DeleteTexts, SaveTexts } from '../base.list.settings';
import { ProviderSettings } from '../provider/provider.settings';

@Component({
    selector: 'user-settings',
    templateUrl: './user.settings.settings.html'
})
export class UserSettings extends BaseListSettings {

    ManagedLocally;
    RolesList = [];
    selectedRole: any;
    selRole: string;

    constructor(private service: UsersService, private translate: TranslateService, private dbUserService: DBUserService,
        private configService: ConfigService, private rolesService: RolesService, protected cdRef: ChangeDetectorRef) {
        super(cdRef);
    }

    getContentType() {
        return UserDetail;
    }

    protected ComponentSet() {
        super.ComponentSet();
        this.rolesService.ReadRoleInfos().subscribe(result => {
            if (result) {
                const roleList = [];
                const otherRoles = [];
                result.forEach(role => {
                    if (role.Id === '0a5d664e-be8d-4a8c-b748-0d4793280d0d') {
                        roleList.splice(0, 0, role);
                    } else if (role.Id === 'df03329c-6538-4918-ba8c-3f743c2928a0') {
                        roleList.push(role);
                    } else {
                        otherRoles.push(role);
                    }
                });
                ArrayHelpers.sortAlphabetical(otherRoles, 'Name');
                roleList.push(...otherRoles);
                if (this.Component) {
                    this.Component.Roles = roleList;
                    this.Component.FilteredRoles = roleList;
                }
                if (roleList) {
                    const list = [];
                    result.forEach((entry) => {
                        list.push({
                            label: entry.Name,
                            value: entry.Id,
                        });
                    });
                    this.RolesList = list;
                }
            }
        });

        if (this.Component) {
            this.Component.ManagedLocally = this.ManagedLocally;
        }
    }

    protected init() {
        this.configService.GetManagementStatus().subscribe((result) => {
            this.ManagedLocally = result;
            this.ShowAdd = result;
            this.ShowDelete = result;
            if (this.Component) {
                this.Component.ManagedLocally = result;
            }
        });
    }

    updateRole(event) {
        if (event.value == null) {
            this.selRole = "";
        }
        else {
            this.selRole = event.value.value;
        }
        this.UpdateFiltered();
    }

    UpdateFiltered() {
        if (this.ListItems) {
            let list = [];

            if (this.ShowFilter && this.SearchValue && this.selRole) {
                const toLower = this.SearchValue.toLowerCase();
                list = this.ListItems.filter(x => x.Caption.toLowerCase().indexOf(toLower) > -1 && x.Roles.includes(this.selRole));

            } else if (this.ShowFilter && this.SearchValue) {
                const toLower = this.SearchValue.toLowerCase();
                list = this.ListItems.filter(x => x.Caption.toLowerCase().indexOf(toLower) > -1);
            } else if (this.ShowFilter && this.selRole) {
                list = this.ListItems.filter(x => x.Roles.includes(this.selRole));
            } else {
                list.push(...this.ListItems);
            }
            this.FilteredItems = list;
        }
    }


    loadList(handler) {
        this.service.ReadUserInfos().subscribe(result => {
            if (result) {
                const list = [];
                result.forEach((entry) => {
                    list.push({
                        Caption: entry.Name,
                        ID: entry.Id,
                        Roles: entry['Roles']
                    });
                });
                handler(list);
            }
        });
    }

    InternalUserFound = false;
    loadData(data) {
        this.service.findUser(data).subscribe(result => {
            if (result) {
                this.InternalUserFound = false;
                if (this.ManagedLocally) {
                    // Delete stays hidden for portal-managed identities -- the portal
                    // owns the account. Save must stay available though: roles are
                    // stored on the local User document and portal admins need to be
                    // able to (un)assign them. The saveInternal guard below ensures
                    // we never call into the local Identity Framework for these.
                    this.ShowDelete = !result['IsManagedByPortal'];
                    this.ShowSave = true;
                }
                if (result.IdentityId) {
                    this.dbUserService.GetUserInfo(result.IdentityId).subscribe(intUser => {
                        this.setSelectedItem(result);
                        this.Component.setInternalUser(intUser);
                        if (intUser) {
                            this.InternalUserFound = true;
                        }
                    });
                } else {
                    this.setSelectedItem(result);
                }
            }
        });
    }

    getNewItem() {
        return {
            DisplayName: this.translate.instant('@@Neuer Benutzer'),
            FirstName: null,
            LastName: null,
            Email: null,
            IsExtern: false,
            IdentityId: null,
            Disabled: false,
            IsAdministrator: false,
            Roles: []
        };
    }

    getDeleteText(sel): DeleteTexts {
        const retVal = new DeleteTexts();
        retVal.Question = new TranslateFormatText('@@Sind Sie sicher, dass Sie den Benutzer \'{0}\' loeschen moechten?');
        retVal.Question.FormatParams.push(sel.Caption);
        retVal.Success = new TranslateFormatText('@@Benutzer \'{0}\' erfolgreich geloescht.');
        retVal.Success.FormatParams.push(sel.Caption);
        retVal.Title = new TranslateFormatText('@@Benutzer loeschen');
        return retVal;
    }

    getSaveSuccessText(sel): SaveTexts {
        let caption = '';
        if (sel.DisplayName) {
            caption = sel.DisplayName;
        } else if (sel.FirstName) {
            caption = sel.FirstName;
            if (sel.LastName) {
                caption += ' ' + sel.LastName
            }
        } else if (sel.LastName) {
            caption = sel.LastName
        }
        const retVal = new SaveTexts();
        retVal.Text = new TranslateFormatText('@@Benutzer \'{0}\' erfolgreich gespeichert.');
        retVal.Text.FormatParams.push(caption);
        retVal.Title = new TranslateFormatText('@@Benutzer speichern');
        return retVal;
    }


    delete(data, handler) {
        this.service.deleteUser(data).subscribe((res) => {
            handler(res);
        });
    }

    protected async checkCanSave(): Promise<DataCheck> {
        var ret = new DataCheck();
        ret.IsCorrect = this.Component.Passwort == this.Component.PasswortCompare;
        return ret;
    }

    saveInternal(item, handler) {
        if (item.IsManagedByPortal) {
            // Portal-managed identity lives in GACentral, not in the local Identity
            // Framework. Skip CreateNewUser/UpdateUser entirely -- otherwise saving
            // a role change would try to provision the portal user locally and
            // fail (or worse, create a shadow account). Only the local User
            // document (roles, IsAdministrator, Disabled, ...) is ours to write.
            this.saveUser(item, handler);
            return;
        }
        if (!item.IdentityId || !this.InternalUserFound) {
            const newUser = {
                UserName: this.Component.Username,
                Email: item.Email,
                Password: this.Component.Password,
                FirstName: item.FirstName,
                LastName: item.LastName
            };
            this.dbUserService.CreateNewUser(newUser).subscribe(result => {
                item.IdentityId = result;
                this.InternalUserFound = true;
                this.saveUser(item, handler);
            });
        } else if (this.Component.Password) {
            const newUser = {
                UserName: this.Component.Username,
                Email: item.Email,
                Password: this.Component.Password,
                FirstName: item.FirstName,
                LastName: item.LastName,
                ID: item.IdentityId
            };
            this.dbUserService.UpdateUser(newUser).subscribe(result => {
                this.saveUser(item, handler);
            });
        } else {
            this.saveUser(item, handler);
        }
    }

    saveUser(item, handler) {
        this.service.createUser(item).subscribe(result => {
            if (result) {
                let caption = result;
                if (item.DisplayName) {
                    caption = item.DisplayName;
                } else if (item.FirstName) {
                    caption = item.FirstName;
                    if (item.LastName) {
                        caption += ' ' + item.LastName
                    }
                } else if (item.LastName) {
                    caption = item.LastName
                }
                handler(result, result, caption);
            }
        });
    }

    handleNew(item, result) {
        item.SID = result;
    }

    static GetSettingsEntry() {
        return {
            Caption: '@@Benutzer',
            ID: 'user',
            Icon: 'user',
            Index: 0,
            Parent: 'userCont',
            Security: {
                Name: 'evidanza.App.Shared.Security.UsermanagementRight',
                Value: 2
            },
            Content: UserSettings
        }
    }
}

@Component({
    selector: 'user-detail',
    templateUrl: './user.settings.html',
    styleUrls: ['./user.settings.css']
})
export class UserDetail extends BaseListDetail implements AfterViewInit {
    TwoFactorEnabled = false;

    _Username;
    get Username() {
        return this._Username;
    }
    set Username(value) {
        this._Username = value;
        if (this.Initialized) {
            this.cdRef.detectChanges();
        }
    }
    _PasswordCompare;
    get PasswordCompare() {
        return this._PasswordCompare;
    }
    set PasswordCompare(value) {
        this._PasswordCompare = value;
        if (this.Initialized) {
            this.cdRef.detectChanges();
        }
    }
    _Password;
    get Password() {
        return this._Password;
    }
    set Password(value) {
        this._Password = value;
        if (this.Initialized) {
            this.cdRef.detectChanges();
        }
    }
    AllProviders = [];
    Roles = [];
    FilteredRoles = [];
    ManagedLocally;
    _RoleSearchValue;
    get RoleSearchValue() {
        return this._RoleSearchValue;
    }
    set RoleSearchValue(value) {
        this._RoleSearchValue = value;
        this.FilteredRoles = this.FilterRoles(this._RoleSearchValue, this._RolesSelectedOnly);
    }

    _RolesSelectedOnly;
    get RolesSelectedOnly() {
        return this._RolesSelectedOnly;
    }
    set RolesSelectedOnly(value) {
        this._RolesSelectedOnly = value;
        this.FilteredRoles = this.FilterRoles(this._RoleSearchValue, this._RolesSelectedOnly);
    }

    constructor(cdRef: ChangeDetectorRef, private service: UsersService,
        private secService: SecurityService, private mailService: MailSettingsService) {
        super();
        this.cdRef = cdRef;
    }
    ngAfterViewInit(): void {
        this.Initialized = true;
    }

    ngOnInit(): void {        
        this.secService.GetSecurityProviders().subscribe(result => {
            if (result) {
                ArrayHelpers.sortAlphabetical(result, 'Name');
                this.AllProviders = result;
            }
        });
    }
    Initialized = false;
    setSelectedItem(item) {
        this._Username = '';
        this._Password = '';
        this._PasswordCompare = '';
        this.TwoFactorEnabled = false;
        if (this.Initialized) {
            this.cdRef.detectChanges();
        }
        super.setSelectedItem(item);
    }
    setInternalUser(authUser) {
        if (authUser) {
            this._Username = authUser.UserName;
            this.TwoFactorEnabled = authUser.TwoFactorEnabled;
            if (this.Initialized) {
                this.cdRef.detectChanges();
            }
        }
    }

    FilterRoles(value, selectedOnly) {
        if (value) {
            if (selectedOnly == true && (!this.SelectedItem || !this.SelectedItem.Roles || this.SelectedItem.Roles.length == 0)) {
                return [];
            }
            value = value.toLocaleLowerCase();
            return this.Roles.filter((roles: any) => {
                return (!value || (value && roles.Name.toLocaleLowerCase().indexOf(value) !== -1)) &&
                    (!selectedOnly || (selectedOnly && this.SelectedItem.Roles.indexOf(roles.Id) > -1));
            });
        } else {
            if (selectedOnly == true) {
                if (this.SelectedItem && this.SelectedItem.Roles && this.SelectedItem.Roles.length > 0) {
                    return this.Roles.filter((roles: any) => {
                        return this.SelectedItem.Roles.indexOf(roles.Id) > -1;
                    });
                } else {
                    return [];
                }
            } else {
                return this.Roles;
            }
        }
    }

    onRoleSelectionChange(ev) {
        if (this.SelectedItem && ev && ev.options) {
            let doFilter = false;
            ev.options.forEach(option => {
                if (option.value) {
                    if (option.selected) {
                        if (this.SelectedItem.Roles) {
                            this.SelectedItem.Roles.push(option.value.Id);
                        } else {
                            this.SelectedItem.Roles = [option.value.Id];
                        }
                    } else {
                        if (this.SelectedItem.Roles) {
                            const index = this.SelectedItem.Roles.indexOf(option.value.Id);
                            if (index >= 0 && index < this.SelectedItem.Roles.length) {
                                this.SelectedItem.Roles.splice(index, 1);
                                if (this._RolesSelectedOnly == true) {
                                    doFilter = true;
                                }
                            }
                        }
                    }
                }
            });
            if (doFilter) {
                this.FilteredRoles = this.FilterRoles(this._RoleSearchValue, this._RolesSelectedOnly);
            }
            this.OnItemChanged();
        }
    }
    PasswordsMatch;
    PasswordChanged() {
        if (this.Password != this.PasswordCompare) {
            this.PasswordsMatch = false;
        } else {
            this.PasswordsMatch = true;
            this.OnItemChanged();
        }
        this.cdRef.detectChanges();
    }
    checkRoleSelection(data) {
        if (data && this.SelectedItem && this.SelectedItem.Roles) {
            return this.SelectedItem.Roles.indexOf(data.Id) > -1;
        }
        return false;
    }
    ResetMFA() {
        if (this.SelectedItem) {
            const sid = this.SelectedItem.SID;
            if (sid) {
                MessageBoxHelper.ShowDialog(new TranslateFormatText('@@MFAResetQuestion'), new TranslateFormatText('@@Question'),
                    MessageBoxButtons.YesNo, MessageBoxIcon.Question).then(x => {
                        if (x == MessageBoxResult.Yes) {
                            this.service.ResetMFA(sid).subscribe(x => {
                                if (x) {
                                    NotificationHelper.Success('@@ResetMFASuccessHeader', '@@ResetMFASuccessContent');
                                    ProviderSettings.ShowMFADialog(this.mailService, [sid]);
                                    this.TwoFactorEnabled = false;
                                    if (this.Initialized) {
                                        this.cdRef.detectChanges();
                                    }
                                }
                            });
                        }
                    });                
            }
        }
    }
}
